import { useEffect, useRef, useState } from "react";
import { HULLS } from "@frontier/content/definitions";
import { equipmentDefinition } from "@frontier/content/arsenal";
import type { EquipmentKey } from "@frontier/content/arsenal";
import type { EquipmentTier } from "@frontier/content/equipment-tiers";
import type { UpgradePricing } from "./use-upgrade-prices";
import type { Profile } from "@frontier/protocol/contracts";
import { EquipmentInventory } from "./EquipmentInventory";
import { EquipmentItemDetail } from "./EquipmentItemDetail";
import { EquipmentSummary } from "./EquipmentSummary";
import { equipmentContextLabel } from "./equipment-selection";
import type { EquipmentContext } from "./equipment-selection";
import {
  installedEquipmentFocus,
  resolveEquipmentFocus,
  sameEquipmentFocus,
} from "./equipment-focus";
import type { EquipmentFocus } from "./equipment-focus";
import { credits } from "./ui-labels";
import "./equipment.css";
import "./equipment-detail.css";
import "./equipment-responsive.css";

interface Props {
  context: EquipmentContext;
  profile: Profile;
  busy: boolean;
  progressionAllowed: boolean;
  upgradePricing: UpgradePricing;
  onContext: (context: EquipmentContext) => void;
  onClose: () => void;
  onSelect: (key: EquipmentKey | null, tier?: EquipmentTier) => Promise<void>;
  onCraft: (key: EquipmentKey) => Promise<void>;
  onDismantle: (key: EquipmentKey, tier: EquipmentTier) => Promise<void>;
  onUpgrade: (key: EquipmentKey, fromTier: EquipmentTier) => Promise<void>;
}

export function EquipmentCatalog(props: Props) {
  const { context, profile, busy } = props;
  const [blueprints, setBlueprints] = useState(false);
  const [focus, setFocus] = useState<EquipmentFocus | null>(() =>
    installedEquipmentFocus(profile, context),
  );
  const installed = installedEquipmentFocus(profile, context);
  const previousInstalled = useRef(installed);
  // Follow a confirmed change only when the inspector was showing that slot.
  useEffect(() => {
    const previous = previousInstalled.current;
    if (!sameEquipmentFocus(previous, installed)) {
      setFocus((current) =>
        sameEquipmentFocus(current, previous) ? installed : current,
      );
      previousInstalled.current = installed;
    }
  }, [installed?.key.kind, installed?.key.id, installed?.tier]);
  const selected = resolveEquipmentFocus(profile, context, focus);
  return (
    <section className="equipment-fitting" aria-label="Подбор оснащения">
      <header className="fitting-heading">
        <div>
          <span>ОСНАЩЕНИЕ / {HULLS[profile.hullId].name}</span>
          <b>{equipmentContextLabel(context)}</b>
        </div>
        <small className="fitting-balance">
          {credits(profile.credits)} КР · {profile.components} КОМП.
        </small>
        <button className="fitting-close" onClick={props.onClose}>
          К КОРАБЛЮ <span aria-hidden="true">×</span>
        </button>
      </header>
      <EquipmentSummary
        profile={profile}
        context={context}
        busy={busy}
        onOpen={props.onContext}
      />
      <div className="fitting-workspace">
        <section
          className="fitting-inventory"
          aria-label="Предметы для выбранного слота"
        >
          <nav className="fitting-filters" aria-label="Фильтр оснащения">
            <button
              aria-pressed={!blueprints}
              onClick={() => setBlueprints(false)}
            >
              НА СКЛАДЕ
            </button>
            {props.progressionAllowed && (
              <button
                aria-pressed={blueprints}
                onClick={() => setBlueprints(true)}
              >
                ВСЕ ЧЕРТЕЖИ
              </button>
            )}
          </nav>
          <div className="fitting-stock-scroll">
          <div className="fitting-acquisition" aria-label="Как получить оснащение">
            {props.progressionAllowed ? blueprints ? (
              <p>Условия получения — в карточке предмета. Компоненты можно получить за контракты, события и доставку груза на станцию.</p>
            ) : (
              <>
                <button type="button" onClick={() => setBlueprints(true)}>ПОЛУЧИТЬ НОВОЕ ОСНАЩЕНИЕ →</button>
                <p>Чертёж → изготовление за компоненты → установка. Условия и цена — в карточке.</p>
              </>
            ) : (
              <p>Во время обучения доступны только предметы со склада. Чертежи и изготовление откроются после выпуска на базу.</p>
            )}
          </div>
            <EquipmentInventory
              context={context}
              profile={profile}
              selected={selected}
              showBlueprints={blueprints && props.progressionAllowed}
              onInspect={setFocus}
            />
          </div>
        </section>
        <div className="fitting-inspector">
          {selected ? (
            <EquipmentItemDetail
              key={`${selected.key.kind}:${selected.key.id}:${selected.tier}`}
              {...props}
              item={equipmentDefinition(selected.key)}
              tier={selected.tier}
            />
          ) : (
            <p className="fitting-empty">
              Ячейка свободна.
            </p>
          )}
          {(profile.shipFitting ? installed !== null : context.kind === "passive" && profile.passiveId !== "none") && (
            <button
              className="fitting-remove"
              disabled={busy}
              onClick={() => void props.onSelect(null)}
            >
              СНЯТЬ ПРЕДМЕТ
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
